import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { listenToShortstayData } from "../../../../redux/shortstaysSlice";
import { listenToShortGuestReservations } from "../../../../redux/shortstayReservationSlice";
import { ChevronLeft, ChevronRight } from "lucide-react";

const GuestShortCancelBooking: React.FC = () => {
  const { guestId, listingId } = useParams<{ guestId: string; listingId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const listing = useSelector((state: RootState) => state.shortstays.currentListing);
  const reservations = useSelector((state: RootState) => state.shortstayReservations.guestReservations);
  const booking = reservations.find(res => res.guestId === guestId && res.listingId === listingId);

  const [monthOffset, setMonthOffset] = useState<number>(0);
  const [selectedCheckOut, setSelectedCheckOut] = useState<Date | null>(null);
  const [selectedNewCheckIn, setSelectedNewCheckIn] = useState<Date | null>(null);
  const [selectedNewCheckOut, setSelectedNewCheckOut] = useState<Date | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>("");

  useEffect(() => {
    if (listingId) dispatch(listenToShortstayData(listingId));
    if (guestId) dispatch(listenToShortGuestReservations(guestId));
  }, [dispatch, listingId, guestId]);

  const today = new Date();
  const currentViewDate = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const currentMonth = currentViewDate.toLocaleString("default", { month: "long" });
  const currentYear = currentViewDate.getFullYear();

  const blockedDates = new Set<string>((listing?.blockedDuration || []).map((d: string) => new Date(d).toDateString()));
  const reservedDates = new Set<string>();
  reservations.forEach((res) => {
    if (res.guestId === guestId && res.listingId === listingId) return;
    const start = new Date(res.checkIn);
    const end = new Date(res.checkOut);
    for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
      reservedDates.add(new Date(d).toDateString());
    }
  });

  const currentResStart = booking ? new Date(booking.checkIn) : new Date();
  const currentResEnd = booking ? new Date(booking.checkOut) : new Date();

  const getMonthDates = (): Date[] => {
    const firstDay = new Date(currentViewDate.getFullYear(), currentViewDate.getMonth(), 1);
    const startDay = (firstDay.getDay() + 6) % 7;
    const start = new Date(firstDay);
    start.setDate(start.getDate() - startDay);

    const dates: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const isAvailable = (date: Date): boolean => {
    const str = date.toDateString();
    return !reservedDates.has(str) && !blockedDates.has(str) && date >= new Date(today.setHours(0, 0, 0, 0));
  };

  const handleDateClick = (date: Date) => {
    const isCheckOutDate = date.toDateString() === currentResEnd.toDateString();

    if (isCheckOutDate) {
      setErrorMsg("Select a day before your current checkout to shorten your stay.");
      setSelectedCheckOut(null);
      return;
    }

    setErrorMsg("");

    if (date > currentResStart && date < currentResEnd) {
      setSelectedCheckOut(date);
      setSelectedNewCheckIn(null);
      setSelectedNewCheckOut(null);
    } else if (isAvailable(date)) {
      if (!selectedNewCheckIn || (selectedNewCheckIn && selectedNewCheckOut)) {
        setSelectedNewCheckIn(date);
        setSelectedNewCheckOut(null);
      } else if (selectedNewCheckIn && !selectedNewCheckOut) {
        if (date > selectedNewCheckIn) {
          let rangeIsValid = true;
          for (let d = new Date(selectedNewCheckIn); d <= date; d.setDate(d.getDate() + 1)) {
            if (!isAvailable(new Date(d))) {
              rangeIsValid = false;
              break;
            }
          }
          if (rangeIsValid) {
            const selectedNights =
              Math.ceil((date.getTime() - selectedNewCheckIn.getTime()) / (1000 * 60 * 60 * 24));
            
            const originalNights = booking?.reservedDates || 1; // fallback to 1 to be safe
          
            if (selectedNights < originalNights) {
              alert("You cannot reschedule to fewer nights than originally booked.");
              return;
            }
          
            setSelectedNewCheckOut(date);
          }
          
        } else {
          setSelectedNewCheckIn(date);
        }
      }
      setSelectedCheckOut(null);
    }
  };

 
  

  const toLocalISODate = (date: Date): string => {
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().split("T")[0];
  };
  
  const handleShorten = () => {
    if (!selectedCheckOut || !booking || !currentResStart) return;
    const checkInStr = toLocalISODate(currentResStart);
    const checkOutStr = toLocalISODate(selectedCheckOut);
    const reservationId = booking.id;
    navigate(`/GuestShortenShortstay/${guestId}/${listingId}/${reservationId}/${checkInStr}/${checkOutStr}`);
  };
  

  const handleReschedule = () => {
    if (!selectedNewCheckIn || !selectedNewCheckOut || !booking) return;
  
    const reservationId = booking.id;
    const toLocalISODate = (date: Date) => {
      const tzOffsetMs = date.getTimezoneOffset() * 60000;
      return new Date(date.getTime() - tzOffsetMs).toISOString().split("T")[0];
    };
    
    const checkInStr = toLocalISODate(selectedNewCheckIn);
    const checkOutStr = toLocalISODate(selectedNewCheckOut);
    
    navigate(`/GuestRescheduleShortstay/${guestId}/${listingId}/${reservationId}/${checkInStr}/${checkOutStr}`);
  };
  

 const handleCancel = () => {
  if (!booking || !guestId || !listingId) return;

  const reservationId = booking.id;
  navigate(`/GuestConfirmCancelShortstay/${guestId}/${listingId}/${reservationId}`);
};


  return (
    <div style={styles.page}>
      <h2 style={styles.title}>Cancel or Reschedule</h2>
      <div style={styles.container}>
        <div style={styles.monthRow}>
          <button onClick={() => setMonthOffset((m) => Math.max(0, m - 1))} style={styles.chevron}>
            <ChevronLeft />
          </button>
          <span style={styles.monthLabel}>{`${currentMonth} ${currentYear}`}</span>
          <button onClick={() => setMonthOffset((m) => Math.min(5, m + 1))} style={styles.chevron}>
            <ChevronRight />
          </button>
        </div>

        <div style={styles.calendarGrid}>
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day, i) => (
            <div key={i} style={styles.dayLabel}>{day}</div>
          ))}

          {getMonthDates().map((date, i) => {
            const isOtherMonth = date.getMonth() !== currentViewDate.getMonth();
            const dateStr = date.toDateString();
            const isCheckIn = dateStr === currentResStart.toDateString();
            const isInCurrentBooking = date > currentResStart && date < currentResEnd;
            const isSelected = selectedCheckOut && dateStr === selectedCheckOut.toDateString();
            const isNewStart = selectedNewCheckIn && dateStr === selectedNewCheckIn.toDateString();
            const isNewEnd = selectedNewCheckOut && dateStr === selectedNewCheckOut.toDateString();
            const isSelectable = isAvailable(date) || isInCurrentBooking || date.toDateString() === currentResEnd.toDateString();

            return (
              <div
                key={i}
                onClick={() => handleDateClick(date)}
                style={{
                  ...styles.dateCard,
                  backgroundColor: isCheckIn
                    ? "#8b0000"
                    : isSelected
                    ? "#bf9b30"
                    : isNewStart || isNewEnd
                    ? "#fff"
                    : isInCurrentBooking
                    ? "#8b0000"
                    : isAvailable(date)
                    ? "#111"
                    : "#333",
                  color: isNewStart || isNewEnd ? "#000" : isSelectable ? "#fff" : "#777",
                  border: isNewStart || isNewEnd ? "2px solid #bf9b30" : "1px solid #222",
                  opacity: isOtherMonth ? 0 : 1,
                  cursor: isSelectable ? "pointer" : "not-allowed",
                  position: "relative",
                }}
              >
                {isCheckIn && <span style={styles.youBadge}>You</span>}
                <div>{date.getDate()}</div>
              </div>
            );
          })}
        </div>

        {errorMsg && <div style={styles.errorMsg}>{errorMsg}</div>}

        <div style={styles.actionRow}>
          {selectedCheckOut && selectedCheckOut < currentResEnd && (
            <button onClick={handleShorten} style={{ ...styles.actionBtn, background: "orange", color: "#000" }}>
              Shorten to {currentResStart.toDateString()} - {selectedCheckOut.toDateString()}
            </button>
          )}

          {selectedNewCheckIn && selectedNewCheckOut && (
            <button onClick={handleReschedule} style={{ ...styles.actionBtn, background: "#fff", color: "#000" }}>
              Reschedule to {selectedNewCheckIn.toDateString()} - {selectedNewCheckOut.toDateString()}
            </button>
          )}
          {booking?.reservationStatus === "cancelled" ? (
  <button disabled style={{ ...styles.actionBtn, background: "#444", color: "#aaa", cursor: "not-allowed" }}>
    Already Cancelled
  </button>
) : (
  <button onClick={handleCancel} style={{ ...styles.actionBtn, background: "#ff4444", color: "#fff" }}>
    Cancel Booking
  </button>
)}


        </div>
      </div>
    </div>
  );
};

export default GuestShortCancelBooking;

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    backgroundColor: "#000",
    minHeight: "100vh",
    padding: "80px 20px 140px",
    color: "#fff",
    fontFamily: "sans-serif",
  },
  title: {
    textAlign: "center",
    fontSize: "1.8rem",
    marginBottom: 20,
  },
  container: {
    background: "#111",
    borderRadius: 16,
    padding: 24,
    maxWidth: 540,
    margin: "0 auto",
    border: "1px solid #bf9b30",
  },
  monthRow: {
    display: "flex",
    justifyContent: "center",
    gap: 20,
    marginBottom: 10,
    alignItems: "center",
  },
  chevron: {
    background: "none",
    border: "none",
    color: "#fff",
    cursor: "pointer",
  },
  monthLabel: {
    fontSize: 16,
    fontWeight: 600,
  },
  calendarGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 10,
    marginTop: 10,
    marginBottom: 20,
  },
  dayLabel: {
    textAlign: "center",
    fontWeight: 600,
    color: "#aaa",
  },
  dateCard: {
    padding: 10,
    textAlign: "center",
    borderRadius: 10,
    transition: "all 0.2s ease",
  },
  youBadge: {
    position: "absolute",
    top: 4,
    right: 6,
    fontSize: "0.6rem",
    fontWeight: "bold",
    background: "red",
    color: "#fff",
    padding: "2px 6px",
    borderRadius: "10px",
  },
  errorMsg: {
    color: "#ffcc00",
    textAlign: "center",
    marginTop: 10,
    fontSize: "0.9rem",
  },
  actionRow: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    marginTop: 20,
  },
  actionBtn: {
    padding: "12px 20px",
    borderRadius: 30,
    fontWeight: 600,
    border: "none",
    fontSize: "1rem",
  },
};
